import { Box, Text, VStack, Link, Flex, Icon, Stack, HStack } from '@chakra-ui/react';
import AuthWrapper from '../../../layouts/AuthWrapper';
import { useForm, Controller } from 'react-hook-form';
import CustomInput from '../../../components/common/Input';
import CustomButton from '../../../components/common/Button';
import Mail from '@/assets/svg/mail.svg?react';
import Lock from '@/assets/svg/lock.svg?react';
import CheckIcon from '@/assets/svg/check-mark-validation.svg?react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { usePostSignUp } from '../../../services/auth/auth';
import { AxiosError } from 'axios';
import useCustomToast from '../../../components/common/Toast';
import { ISignUpResponse } from './../../../services/auth/types';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '@/routes/constants';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
}

/* ---------- Yup schema (email + password rules) ---------- */
const schema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required'),

  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Must be at least 8 characters')
    .matches(/^(?=.*[!@#$%^&*])/, 'Must contain at least one special character (!@#$%^&*)'),

  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

const SignUp = () => {
  const { mutateAsync: signUp } = usePostSignUp();
  const { showToast } = useCustomToast();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting, isSubmitted },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  });

  /* ---------- Live password validation for checklist ---------- */
  const password = watch('password') ?? '';
  const isMinLengthValid = password.length >= 8;
  const hasSpecialChar = /[!@#$%^&*]/.test(password);

  const onSubmit = (data: FormData) => {
    const payload = {
      email: data.email,
      password: data.password,
      confirm_password: data.confirmPassword,
    };

    signUp(payload)
      .then((res: ISignUpResponse) => {
        showToast('success', res?.msg ?? 'Verification code sent to your email');
        navigate(NAVIGATION_ROUTES.EMAILVERIFICATION, {
          state: { email: res.email ?? data.email },
        });
      })
      .catch((error: unknown) => {
        if (error instanceof AxiosError) {
          const msg =
            error?.response?.data?.email?.[0] ??
            error?.response?.data?.password?.[0] ??
            error?.response?.data?.error ??
            'Something went wrong';

          // Show generic toast + field error if it belongs to a field
          showToast('error', msg);
          if (error?.response?.data?.email) {
            setError('email', { type: 'manual', message: msg });
          }
          if (error?.response?.data?.password) {
            setError('password', { type: 'manual', message: msg });
          }
        } else {
          showToast('error', 'Network error. Please check your internet connection');
        }
      });
  };

  return (
    <AuthWrapper>
      <Box w="full" maxW="lg" p={8} bg="white" fontFamily={'primary'}>
        <VStack spacing={8} align="stretch">
          {/* ---------- Header ---------- */}
          <Flex direction={'column'} gap={1}>
            <Text color={'text.primary'} fontSize="4xl" fontWeight={'semibold'}>
              Create An Account
            </Text>
            <Text color={'text.secondary'} fontSize={'sm'} fontWeight={'normal'}>
              Please enter your details to get started.
            </Text>
          </Flex>

          {/* ---------- Form ---------- */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5} align="stretch">
              {/* ---- Email ---- */}
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Email"
                    leftIcon={
                      <Icon boxSize={5} color={'text.secondary'}>
                        <Mail />
                      </Icon>
                    }
                    error={isSubmitted ? errors.email : undefined}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.email) clearErrors('email');
                    }}
                    placeholder="Enter your email"
                    type="email"
                  />
                )}
              />

              {/* ---- Password ---- */}
              <Controller
                name="password"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Password"
                    type="password"
                    placeholder="Enter password"
                    leftIcon={<Icon as={Lock} boxSize={5} color={'text.secondary'} />}
                    error={errors.password}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.password) clearErrors('password');
                    }}
                  />
                )}
              />

              {/* ---- Confirm Password ---- */}
              <Controller
                name="confirmPassword"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <CustomInput
                    {...field}
                    label="Confirm Password"
                    type="password"
                    placeholder="Confirm password"
                    leftIcon={<Icon as={Lock} boxSize={5} color={'text.secondary'} />}
                    error={errors.confirmPassword}
                    onChange={(e) => {
                      field.onChange(e);
                      if (errors.confirmPassword) clearErrors('confirmPassword');
                    }}
                  />
                )}
              />

              {/* ---- Password Guidelines (live checklist) ---- */}
              <Stack spacing={1} mt={2}>
                <HStack>
                  <Box
                    bg={isMinLengthValid ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={1.5}
                    h="14px"
                    w="14px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={CheckIcon} color="white" fontSize="8px" />
                  </Box>
                  <Text fontSize="xs" color="text.secondary">
                    Must be at least 8 characters
                  </Text>
                </HStack>

                <HStack>
                  <Box
                    bg={hasSpecialChar ? 'status.success' : 'border.default'}
                    borderRadius="full"
                    p={1.5}
                    h="14px"
                    w="14px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={CheckIcon} color="white" fontSize="8px" />
                  </Box>
                  <Text fontSize="xs" color="text.secondary">
                    Must contain one special character (!@#$%^&*)
                  </Text>
                </HStack>
              </Stack>

              {/* ---- Submit ---- */}
              <CustomButton
                isLoading={isSubmitting}
                type="submit"
                width="full"
                isDisabled={!isValid}
              >
                Sign up
              </CustomButton>
            </VStack>
          </form>

          {/* ---------- Footer link ---------- */}
          <Text fontSize="sm" textAlign="center" color="gray.500">
            Already have an account?{' '}
            <Link
              href={NAVIGATION_ROUTES.SIGNIN}
              color="brand.primary.DEFAULT"
              fontWeight="semibold"
            >
              Sign in
            </Link>
          </Text>
        </VStack>
      </Box>
    </AuthWrapper>
  );
};

export default SignUp;
